import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["lowerBound", "upperBound"];
import { ANNUALLY, MONTHLY, ONE_TIME_FEE } from 'pricing-pages-lib/constants/CalculatedPriceTerms';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { getProductsFromProductInventoryQuantity } from 'pricing-pages-lib/utils/productInventoryUtils';
import { getRequiredOnboardingSkuIds } from 'pricing-pages-lib/utils/requiredOnboardingUtils';
import { calculatedPriceAdapterV3 } from 'self-service-api/adapters/calculatedPriceAdapter';
import { HUBSPOT_FOR_STARTUPS_SETUP } from 'self-service-api/constants/ApiNames';
import { TIERED } from 'self-service-api/constants/PricingTypes';
import { STARTER, currentOrderedPaidProductLevels } from 'self-service-api/constants/ProductLevels';
import { QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { getIsUngatedForSeats } from './gatingUtils';
import { getCrmSuiteLowestTermsOfferId, getCrmSuiteSeatsLowestTermsOfferId, getLowestTermsOfferId, getOfferIdForSkuInProductConfigurations, getOfferInfo } from './offerUtils';
import { getCrmSuiteProductLevelForProductConfigurations, getIsMarketingContactsSku, getProductForSkuId, getProductVersion, getSku, getSkuIdIsHub, getSkuIdIsInCrmSuite, isQuantifiableHub } from './productsUtils';
import { hasUnbundledCrmSuiteOfferIdsInPromotions } from './promotionUtils';
const calculatePrice = ({
  productData: {
    skuMap,
    productMap
  }
}, {
  purchaseConfig
}) => {
  const calculatedPrice = calculatedPriceAdapterV3({
    purchaseConfig,
    productMap,
    skuMap
  });
  return Object.assign({}, purchaseConfig.productConfigurations[0], calculatedPrice);
};
const createPurchaseConfig = ({
  currencyCode,
  offerInfo,
  productConfigurations,
  offerId
}) => {
  const {
    discount: discountPercentage
  } = offerInfo || {};
  return {
    currencyCode,
    productConfigurations,
    offerId,
    discountPercentage
  };
};

// Used to transform the calculated price object when we want to position discounted
// price as the base price.
const transformDiscountedPriceAndOfferConfigToBase = (state, {
  offerId,
  lowestTermsOfferId,
  productConfiguration,
  displayedCurrencyCode
}) => {
  const currentTermsCalculatedPrice = calculatePrice(state, {
    purchaseConfig: createPurchaseConfig({
      currencyCode: displayedCurrencyCode,
      productConfigurations: [productConfiguration],
      offerId,
      offerInfo: getOfferInfo(state, {
        offerId
      })
    })
  });
  const lowestTermsCalculatedPrice = calculatePrice(state, {
    purchaseConfig: createPurchaseConfig({
      currencyCode: displayedCurrencyCode,
      productConfigurations: [productConfiguration],
      offerId: lowestTermsOfferId,
      offerInfo: getOfferInfo(state, {
        offerId: lowestTermsOfferId
      })
    })
  });
  const priceConfigKeys = [MONTHLY, ANNUALLY, ONE_TIME_FEE];
  const calculatedPrice = Object.assign({}, currentTermsCalculatedPrice, priceConfigKeys.reduce((acc, curr) => {
    const fullPrice = lowestTermsCalculatedPrice[curr].calculatedPrice;
    acc[curr] = Object.assign({}, currentTermsCalculatedPrice[curr], {
      fullPrice
    });
    return acc;
  }, {}));
  const newDiscount = (1 - calculatedPrice['monthly'].calculatedPrice / calculatedPrice['monthly'].fullPrice) * 100;
  const offerInfo = Object.assign({}, getOfferInfo(state, {
    offerId
  }), {
    discount: newDiscount
  });
  return Object.assign({}, calculatedPrice, {
    offerInfo
  });
};

/**
 * This function merges multiple price objects into a single price object.
 * The resulting totalPrices object contains the sum of all prices from all input price objects.
 *
 * @param priceObjects - An array of price objects to be merged.
 * @returns A single price object that is the result of merging all input price objects.
 */
export const mergePriceObjects = (...priceObjects) => {
  // Filter out any undefined or null price objects from the provided array
  const validPriceObjects = priceObjects.filter(priceObject => Boolean(priceObject));
  const totalPrices = Object.assign({}, validPriceObjects[0], {
    monthly: {
      fullPrice: 0,
      calculatedPrice: 0
    },
    annually: {
      fullPrice: 0,
      calculatedPrice: 0
    },
    oneTimeFee: {
      fullPrice: 0,
      calculatedPrice: 0
    }
  });
  validPriceObjects.forEach(priceObject => {
    if (priceObject) {
      [MONTHLY, ANNUALLY, ONE_TIME_FEE].forEach(section => {
        const castedSection = section;
        totalPrices[castedSection].fullPrice = totalPrices[castedSection].fullPrice + priceObject[castedSection].fullPrice;
        totalPrices[castedSection].calculatedPrice = totalPrices[castedSection].calculatedPrice + priceObject[castedSection].calculatedPrice;
      });
    }
  });
  return totalPrices;
};
export const subtractPriceObjects = (...priceObjects) => {
  const totalPrices = priceObjects.length ? JSON.parse(JSON.stringify(priceObjects[0])) : {};
  priceObjects.slice(1).forEach(priceObject => {
    if (priceObject) {
      [MONTHLY, ANNUALLY, ONE_TIME_FEE].forEach(section => {
        const castedSection = section;
        totalPrices[castedSection].fullPrice = totalPrices[castedSection].fullPrice - priceObject[castedSection].fullPrice;
        totalPrices[castedSection].calculatedPrice = totalPrices[castedSection].calculatedPrice - priceObject[castedSection].calculatedPrice;
      });
    }
  });
  return totalPrices;
};
const getProductsFromProductInventoryQuantityForShowingAdditionalPrice = (state, {
  productConfiguration,
  allProductConfigurations
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const sku = skuMap[productConfiguration.skuId];
  if (sku.skuType === QUANTITY_PACK) {
    const {
      productVersionSkuId
    } = sku;
    if (!allProductConfigurations.some(({
      skuId
    }) => skuId === productVersionSkuId)) {
      const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
        skuId: sku.skuId
      });
      return ownedQuantity;
    }
  }
  return productConfiguration.quantity;
};

/**
 * Returns the price breakdown for a specific product configuration in a list of product
 * configurations.
 */
export const getPriceBreakdownForProductConfigurationInList = (state, {
  allProductConfigurations,
  productConfiguration,
  queryParamTerm,
  adjustPricesForOwnership = false,
  displayedCurrencyCode
}) => {
  const suiteProductLevel = getCrmSuiteProductLevelForProductConfigurations(state, {
    productConfigurations: allProductConfigurations
  });
  const hasUnbundledCrmSuiteOfferIds = hasUnbundledCrmSuiteOfferIdsInPromotions(state);
  const isUngatedForSeats = getIsUngatedForSeats(state);
  const productVersion = getProductVersion(state, {
    skuId: productConfiguration.skuId
  });
  if (!productVersion) throw new Error(`productVersion was undefined for skuId ${productConfiguration.skuId}`);
  const {
    skuId
  } = productVersion;
  const {
    productLevel,
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const offerIdToUse = getOfferIdForSkuInProductConfigurations(state, {
    allProductConfigurations,
    productConfiguration,
    queryParamTerm
  });
  const offerInfo = offerIdToUse ? getOfferInfo(state, {
    offerId: typeof offerIdToUse === 'number' ? offerIdToUse : offerIdToUse[productLine]
  }) : null;
  if (!productConfiguration || !offerIdToUse) {
    return null;
  }
  const purchaseConfig = createPurchaseConfig({
    currencyCode: displayedCurrencyCode,
    productConfigurations: [productConfiguration],
    offerInfo: getOfferInfo(state, {
      offerId: typeof offerIdToUse === 'number' ? offerIdToUse : offerIdToUse[productLine]
    }),
    offerId: offerIdToUse
  });
  let calculatedPrice = calculatePrice(state, {
    purchaseConfig
  });
  if (adjustPricesForOwnership && 'productInventory' in state) {
    const productsFromProductInventoryQuantity = getProductsFromProductInventoryQuantityForShowingAdditionalPrice(state, {
      productConfiguration,
      allProductConfigurations
    });
    if (productsFromProductInventoryQuantity !== productConfiguration.quantity) {
      const ownedPurchaseConfig = createPurchaseConfig({
        currencyCode: displayedCurrencyCode,
        productConfigurations: [createProductConfig({
          skuId: productConfiguration.skuId,
          quantity: productsFromProductInventoryQuantity
        })],
        offerInfo: getOfferInfo(state, {
          offerId: typeof offerIdToUse === 'number' ? offerIdToUse : offerIdToUse[productLine]
        }),
        offerId: offerIdToUse
      });
      const ownedPrice = calculatePrice(state, {
        purchaseConfig: ownedPurchaseConfig
      });
      calculatedPrice = Object.assign({}, subtractPriceObjects(calculatedPrice, ownedPrice), {
        quantity: calculatedPrice.quantity - productsFromProductInventoryQuantity
      });
    }
  }
  const skuIdIsHub = getSkuIdIsHub(state, {
    skuId: productConfiguration.skuId
  });
  const skuIdIsQuantifiableHub = isUngatedForSeats && isQuantifiableHub(state, {
    skuId: productConfiguration.skuId
  });
  const isStarterAlaCarte = productLevel === STARTER && !suiteProductLevel;
  const isCrmSuiteHub = suiteProductLevel && hasUnbundledCrmSuiteOfferIds && skuIdIsHub && getSkuIdIsInCrmSuite(state, {
    skuId,
    suiteProductLevel
  });
  if (isStarterAlaCarte || isCrmSuiteHub) {
    const lowestTermsOfferId = suiteProductLevel ? skuIdIsQuantifiableHub ? getCrmSuiteSeatsLowestTermsOfferId(state, {
      productLevel: suiteProductLevel || productLevel
    }) : getCrmSuiteLowestTermsOfferId(state, {
      productLevel: suiteProductLevel || productLevel
    }) : getLowestTermsOfferId(state, {
      skuId
    });
    return transformDiscountedPriceAndOfferConfigToBase(state, {
      offerId: typeof offerIdToUse === 'number' ? offerIdToUse : offerIdToUse[productLine],
      lowestTermsOfferId,
      productConfiguration,
      displayedCurrencyCode
    });
  }
  return Object.assign({}, calculatedPrice, {
    offerInfo
  });
};
export const getPriceBreakdown = (state, {
  productConfigurations,
  queryParamTerm,
  adjustPricesForOwnership = false,
  displayedCurrencyCode
}) => {
  return productConfigurations.map(productConfiguration => {
    return getPriceBreakdownForProductConfigurationInList(state, {
      allProductConfigurations: productConfigurations,
      productConfiguration,
      queryParamTerm,
      adjustPricesForOwnership,
      displayedCurrencyCode
    });
  }).filter(priceObject => Boolean(priceObject));
};
export const getCalculatedPrice = (state, args) => {
  const priceBreakdown = getPriceBreakdown(state, args);
  return mergePriceObjects(...priceBreakdown);
};

/**
 * Groups required onboarding products by product level.  Used for the POP migration RequiredOnboarding component
 *
 * @param skuIds A list of skuIds
 * @returns skuIds of the required onboarding products for the given skuIds mapped to the product level of the skuId
 */
export const getRequiredOnboardingSkuIdsByProductLevel = (state, {
  skuIds
}) => {
  const USE_DIFFERENT_COPY = [HUBSPOT_FOR_STARTUPS_SETUP];
  const {
    productData: {
      skuMap
    }
  } = state;
  return skuIds.reduce((acc, skuId) => {
    const {
      skuType
    } = skuMap[skuId];
    const requiredOnboardingSkuIds = getRequiredOnboardingSkuIds(state, {
      skuId
    });
    if (requiredOnboardingSkuIds && requiredOnboardingSkuIds.length && skuType !== QUANTITY_PACK) {
      requiredOnboardingSkuIds.forEach(requiredOnboardingSkuId => {
        const {
          productLevel
        } = getProductForSkuId(state, {
          skuId
        });
        const {
          apiName: onboardingApiName
        } = getProductForSkuId(state, {
          skuId: requiredOnboardingSkuId
        });
        if (USE_DIFFERENT_COPY.includes(onboardingApiName)) {
          acc[onboardingApiName] = [...(acc[onboardingApiName] || []), requiredOnboardingSkuId];
        } else if (currentOrderedPaidProductLevels.includes(productLevel)) {
          acc[productLevel] = [...(acc[productLevel] || []), requiredOnboardingSkuId];
        }
      });
    }
    return acc;
  }, {});
};
export const getTierPriceMap = (state, {
  skuId,
  queryParamTerm,
  allProductConfigurations,
  displayedCurrencyCode
}) => {
  const sku = getSku(state, {
    skuId
  });
  if (sku.pricingWrap.pricingType !== TIERED) {
    return null;
  }
  const productVersion = getProductVersion(state, {
    skuId
  });
  const {
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const isMarketingContactsSku = getIsMarketingContactsSku(state, {
    skuId: productVersion.skuId
  });
  const pricingInfo = sku.pricingWrap.tieredPrices.find(({
    currencyCode: searchCurrencyCode
  }) => displayedCurrencyCode === searchCurrencyCode);
  if (!pricingInfo) throw new Error(`Cannot find priceInfo for quantity pack skuId ${skuId}`);
  let priceTiers = pricingInfo.priceTiers;
  let offerId = null;
  if (queryParamTerm) {
    offerId = getOfferIdForSkuInProductConfigurations(state, {
      allProductConfigurations,
      productConfiguration: createProductConfig({
        skuId,
        quantity: 1
      }),
      queryParamTerm
    });
  }
  if (!offerId && !queryParamTerm) {
    offerId = getLowestTermsOfferId(state, {
      skuId
    });
  }
  if (offerId) {
    const {
      discount
    } = getOfferInfo(state, {
      offerId: typeof offerId === 'number' ? offerId : offerId[productLine]
    }) || {
      discount: 0
    };
    priceTiers = priceTiers.map(priceTier => Object.assign({}, priceTier, {
      value: priceTier.value * (1 - discount / 100)
    }));
  }
  let includedTierUnits = 0;
  // Need to create an artificial first tier in the prices
  // for included units on marketing contacts skus
  if (isMarketingContactsSku) {
    const {
      includedWithProductVersion,
      includedWithQuantityPack
    } = sku;
    includedTierUnits = includedWithProductVersion / includedWithQuantityPack;
  }
  priceTiers = priceTiers.map(_ref => {
    let {
        lowerBound,
        upperBound
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return Object.assign({}, rest, {
      lowerBound: lowerBound + includedTierUnits,
      upperBound: upperBound == null ? null : upperBound + includedTierUnits
    });
  });

  // Annoyingly, only the first lower bound in price tiers is inclusive
  // so gotta make it exclusive when bumped up
  if (priceTiers.length > 0) priceTiers[0].lowerBound++;
  priceTiers.unshift({
    lowerBound: 0,
    upperBound: includedTierUnits,
    value: 0
  });
  return priceTiers;
};